import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  Keyboard,
  Mic,
  MessageSquare,
  Minus,
  Pencil,
} from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Badges de estado.
 *
 * Regra de acessibilidade herdada do handoff: status NUNCA é comunicado só por
 * cor. Todo badge combina ícone + texto + cor, garantindo AA para quem não
 * distingue verde de vermelho.
 */

type Variante = 'entrada' | 'saida' | 'pendente' | 'confirmado' | 'editado' | 'excluido';

const ESTILOS: Record<Variante, { classe: string; icone: ReactNode; rotulo: string }> = {
  entrada: {
    classe: 'bg-entrada-bg text-entrada-fg border-entrada-borda',
    icone: <ArrowUp size={11} strokeWidth={2.5} aria-hidden />,
    rotulo: 'ENTRADA',
  },
  saida: {
    classe: 'bg-saida-bg text-saida-fg border-saida-borda',
    icone: <ArrowDown size={11} strokeWidth={2.5} aria-hidden />,
    rotulo: 'SAÍDA',
  },
  // Fundo sólido e peso 700: é o estado que não pode passar despercebido.
  pendente: {
    classe: 'bg-pendente text-pendente-fg border-pendente-borda font-bold',
    icone: <AlertTriangle size={11} strokeWidth={2.5} aria-hidden />,
    rotulo: 'PENDENTE DE REVISÃO',
  },
  confirmado: {
    classe: 'bg-entrada-bg text-entrada-fg border-entrada-borda',
    icone: <Check size={11} strokeWidth={2.5} aria-hidden />,
    rotulo: 'CONFIRMADO',
  },
  editado: {
    classe: 'bg-primaria-bg text-primaria-fg border-primaria-borda',
    icone: <Pencil size={11} strokeWidth={2.5} aria-hidden />,
    rotulo: 'EDITADO',
  },
  excluido: {
    classe: 'bg-transparent text-texto-apagado border-borda-input border-dashed',
    icone: <Minus size={11} strokeWidth={2.5} aria-hidden />,
    rotulo: 'EXCLUÍDO',
  },
};

export function Badge({ variante, texto }: { variante: Variante; texto?: string }) {
  const { classe, icone, rotulo } = ESTILOS[variante];
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-[var(--radius-badge)] border px-2 py-0.5 text-[10px] font-semibold ${classe}`}
    >
      {icone}
      {texto ?? rotulo}
    </span>
  );
}

/** De onde veio o lançamento — texto, áudio ou digitado no painel. */
export function BadgeOrigem({ origem }: { origem: 'texto' | 'audio' | 'manual' }) {
  const mapa = {
    texto: { icone: <MessageSquare size={11} aria-hidden />, rotulo: 'TEXTO' },
    audio: { icone: <Mic size={11} aria-hidden />, rotulo: 'ÁUDIO' },
    manual: { icone: <Keyboard size={11} aria-hidden />, rotulo: 'MANUAL' },
  } as const;
  const { icone, rotulo } = mapa[origem];

  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-[var(--radius-badge)] border border-neutro-borda bg-neutro-bg px-2 py-0.5 text-[10px] font-semibold text-neutro-fg">
      {icone}
      {rotulo}
    </span>
  );
}
